import type {
	DynamicFormSchema,
	PersistedDynamicFieldDefinition,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import {
	type LayoutElement,
	LayoutTypes,
	parseFormLayoutUnknown,
} from "@/lib/layoutRenderer/schemas/layout.schema";
import type {
	FormDefinition,
	FormDefinitionQueryData,
} from "@/services/form-definition";
import type { LayoutJsonEnvelope } from "@/services/form-definition/layout-json-envelope.schema";
import { parseLayoutJsonWire } from "@/services/form-definition/layout-json-envelope.schema";
import { resolveFormRulesFromFormDefinition } from "@/services/form-definition/resolve-form-definition-form-rules.util";
import {
	type FormRule,
	FormRulesEnvelopeSchema,
} from "../types/form-rule-types";

/**
 * API row shape for {@link buildDynamicFormSchemaFromApiFormDefinition}.
 * Wire responses use `layoutJson` as a string; TanStack-query `select` supplies parsed layout + envelope.
 */
export type BuildDynamicFormSchemaApiInput = Pick<
	FormDefinition,
	"fieldDefinitions" | "formRules"
> & {
	layoutJson?: string | LayoutElement | null;
	layout?: LayoutElement | null;
	layoutEnvelope?: LayoutJsonEnvelope | null;
};

/**
 * Merges `fieldDefinitions` from the API row and from a legacy parsed envelope (by `id`).
 */
function resolveFieldDefinitions(
	api: Pick<FormDefinition, "fieldDefinitions">,
	legacyEnvelope: Record<string, unknown> | null,
): DynamicFormSchema["fieldDefinitions"] {
	const merged = new Map<string, PersistedDynamicFieldDefinition>();

	function pushList(list: unknown): void {
		if (!Array.isArray(list)) return;
		for (const row of list) {
			if (
				row !== null &&
				typeof row === "object" &&
				"id" in row &&
				typeof (row as { id: unknown }).id === "string"
			) {
				const d = row as PersistedDynamicFieldDefinition;
				merged.set(d.id, d);
			}
		}
	}

	pushList(api.fieldDefinitions);
	pushList(legacyEnvelope?.fieldDefinitions);

	return [...merged.values()];
}

/**
 * Layout `Field` nodes whose `fieldDefinitionId` does not match any definition `id` or `name`.
 */
export function listMissingFieldDefinitionIdsForLayout(
	layout: LayoutElement,
	definitions: PersistedDynamicFieldDefinition[],
): string[] {
	const byId = new Set(definitions.map((d) => d.id));
	const byName = new Set(definitions.map((d) => d.name));
	const missing: string[] = [];

	function walk(node: LayoutElement): void {
		if (node.type === LayoutTypes.Field) {
			const key = node.fieldDefinitionId;
			if (!byId.has(key) && !byName.has(key)) {
				missing.push(key);
			}
			return;
		}
		if ("elements" in node) {
			for (const el of node.elements) {
				walk(el);
			}
		}
	}

	walk(layout);
	return missing;
}

export interface BuildSchemaFromApiFormDefinitionResult {
	schema: DynamicFormSchema;
	/** Stringified layout subtree for persistence / API payload helpers (not the full envelope). */
	layoutJsonForSchema: string;
	/** Resolved form rules — never `null`, defaults to `[]`. */
	formRules: FormRule[];
}

/**
 * Reads and validates a `formRules` array off a legacy parsed `layoutJson` envelope.
 * Returns `[]` when the field is missing, malformed, or fails Zod validation.
 */
export function extractFormRulesFromEnvelope(
	envelope: Record<string, unknown> | null | undefined,
): FormRule[] {
	if (!envelope) return [];
	const raw = envelope.formRules;
	if (!Array.isArray(raw)) return [];
	const parsed = FormRulesEnvelopeSchema.safeParse(raw);
	if (!parsed.success) return [];
	return parsed.data;
}

function isQueryShapedInput(
	api: BuildDynamicFormSchemaApiInput,
): api is FormDefinitionQueryData {
	return "layout" in api;
}

/**
 * Builds a {@link DynamicFormSchema} from GET `/form-definitions/current` (or similar) payload.
 *
 * @returns `null` when layout is missing/invalid or no layout can be resolved.
 */
export function buildDynamicFormSchemaFromApiFormDefinition(
	formSchemaId: string,
	api: BuildDynamicFormSchemaApiInput,
): BuildSchemaFromApiFormDefinitionResult | null {
	let layoutTree: LayoutElement | null = null;
	let typedEnvelope: LayoutJsonEnvelope | null = null;
	let legacyEnvelope: Record<string, unknown> | null = null;

	if (isQueryShapedInput(api)) {
		layoutTree = api.layout;
		typedEnvelope = api.layoutEnvelope;
	} else if (api.layout != null) {
		const validated = parseFormLayoutUnknown(api.layout);
		if (!validated.ok) return null;
		layoutTree = validated.layout;
		typedEnvelope = api.layoutEnvelope ?? null;
	} else {
		const rawLayout = api.layoutJson;
		if (rawLayout === null || rawLayout === undefined) {
			return null;
		}

		if (typeof rawLayout === "string") {
			const trimmed = rawLayout.trim();
			if (!trimmed) return null;
			const parsed = parseLayoutJsonWire(trimmed);
			if (!parsed) return null;
			layoutTree = parsed.envelope.layout;
			typedEnvelope = parsed.envelope;
			try {
				legacyEnvelope = JSON.parse(trimmed) as Record<string, unknown>;
			} catch {
				legacyEnvelope = null;
			}
		} else {
			const validated = parseFormLayoutUnknown(rawLayout);
			if (!validated.ok) return null;
			layoutTree = validated.layout;
			typedEnvelope = api.layoutEnvelope ?? null;
		}
	}

	if (layoutTree === null) return null;

	const fieldDefinitions = resolveFieldDefinitions(api, legacyEnvelope);
	const formRules = resolveFormRulesFromFormDefinition({
		formRules: api.formRules,
		layoutJson: typeof api.layoutJson === "string" ? api.layoutJson : null,
	});

	const schema: DynamicFormSchema = {
		id: formSchemaId,
		title: typedEnvelope?.title || undefined,
		description: typedEnvelope?.description || undefined,
		fieldDefinitions,
		layoutJson: layoutTree,
		...(formRules.length > 0 ? { rules: formRules } : {}),
	};

	return {
		schema,
		layoutJsonForSchema: JSON.stringify(layoutTree),
		formRules,
	};
}
